/**
 * Zod schema for the edit persona form.
 * Single source of truth for types, validation, tab configuration, and data mapping.
 */
import type { UpdatePersonaDto } from '@inference.engine/persona-api-client';
import type { PersonaWorkspacesDto } from '@inference.engine/api-client/web';
import { z } from 'zod';
import type { TFunction } from 'i18next';

import { BIOGRAPHY_ITEM_LABELS } from '@/features/audiences/model';
import {
  type SpaceVisibilityValue,
  spaceVisibilityValues,
} from '../PersonaFormSections/SpaceVisibilityField';
import type { PersonaDto } from '@/lib/api/personas-client';
import { i18n } from '@/shared/i18n';

import { getBiographyItems, mapAgeToRange, normalizeGender } from '../../lib';

const BIOGRAPHY_ITEM_COUNT = 4;

/**
 * Main form schema.
 *
 * A factory, not a module-scope const: building the schema at import time would bake
 * in whatever language was active then, and the messages could never follow a change.
 */
export const buildEditPersonaFormSchema = (t: TFunction<'validation'>) => {
  const itemIsRequired = t('audiences.editPersonaForm.itemIsRequired');
  const descriptionIsRequired = t('audiences.editPersonaForm.descriptionIsRequired');

  // Biography item (Item + Description).
  const biographyItemSchema = z.object({
    item: z.string().min(1, itemIsRequired),
    description: z.string().min(1, descriptionIsRequired),
  });

  // Cares-about item (Item + Description).
  const caresAboutItemSchema = z.object({
    item: z.string().min(1, itemIsRequired),
    description: z.string().min(1, descriptionIsRequired),
  });

  // Pain item (Description only).
  const painItemSchema = z.object({
    description: z.string().min(1, descriptionIsRequired),
  });

  // Gain item (Description only).
  const gainItemSchema = z.object({
    description: z.string().min(1, descriptionIsRequired),
  });

  // Channel (platformName and sharePercent are read-only). Description is only
  // required when sharePercent > 0.
  const channelSchema = z
    .object({
      platformName: z.string(),
      sharePercent: z.number(),
      description: z.string(),
    })
    .refine((channel) => channel.sharePercent === 0 || channel.description.length > 0, {
      message: descriptionIsRequired,
      path: ['description'],
    });

  const schema = z.object({
    // Basic Information section
    personaTitle: z.string().optional(),
    segment: z.string().min(1, t('audiences.editPersonaForm.segmentIsRequired')),
    quote: z.string().min(1, t('audiences.editPersonaForm.quoteIsRequired')),

    // Biography section
    interests: z.array(z.string()),
    biographyItems: z.array(biographyItemSchema).length(BIOGRAPHY_ITEM_COUNT),

    // Demographics section
    age: z.string().min(1, t('audiences.editPersonaForm.ageIsRequired')),
    gender: z.string().min(1, t('audiences.editPersonaForm.genderIsRequired')),
    location: z.string().min(1, t('audiences.editPersonaForm.locationIsRequired')),
    education: z.string().min(1, t('audiences.editPersonaForm.educationIsRequired')),
    familyComposition: z
      .string()
      .min(1, t('audiences.editPersonaForm.familyCompositionIsRequired')),
    annualIncome: z.string().min(1, t('audiences.editPersonaForm.annualIncomeIsRequired')),
    lifeStage: z.string().min(1, t('audiences.editPersonaForm.lifeStageIsRequired')),

    // Cares about section
    caresAbout: z
      .array(caresAboutItemSchema)
      .min(1, t('audiences.editPersonaForm.atLeastOneItemIsRequired')),

    // Channels section (from API data)
    channels: z.array(channelSchema),

    // Pains section
    pains: z.array(painItemSchema).min(1, t('audiences.editPersonaForm.atLeastOnePainIsRequired')),

    // Gains section
    gains: z.array(gainItemSchema).min(1, t('audiences.editPersonaForm.atLeastOneGainIsRequired')),

    // Profile Media
    photoAssetId: z.string().nullable(),
    mouseOverAssetId: z.string().nullable(),
    videoAssetId: z.string().nullable(),

    // GWI audience link (optional) — empty string is treated as "unset"
    gwiId: z
      .uuid(t('audiences.editPersonaForm.mustBeAValidGwiAudienceUuid'))
      .or(z.literal(''))
      .nullable()
      .optional(),

    // Space Visibility
    spaceVisibility: z
      .enum(spaceVisibilityValues)
      .nullable()
      .refine((val): val is SpaceVisibilityValue => val !== null, {
        message: t('audiences.editPersonaForm.spaceVisibilityIsRequired'),
      }),

    workspaceIds: z.array(z.uuid()),
  });

  // A persona narrowed to specific workspaces with none picked reaches the API as a scope
  // visible to nobody, which it rejects; the create schema guards the same case.
  return schema.superRefine((data, ctx) => {
    if (data.spaceVisibility === 'workspaces' && data.workspaceIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['workspaceIds'],
        message: t('audiences.editPersonaForm.pickAtLeastOneWorkspace'),
      });
    }
  });
};

/** The scope endpoint has no counterpart for "my own workspace": it is a grant like any other. */
export function toScopeVisibility(
  value: SpaceVisibilityValue | null,
): PersonaWorkspacesDto['visibility'] {
  return value === 'organization' ? 'organization' : 'workspaces';
}

/**
 * Form data type.
 */
export type EditPersonaFormData = z.input<ReturnType<typeof buildEditPersonaFormSchema>>;

/**
 * Tab configuration for Navrail.
 */
interface FormTab {
  id: string;
  label: string;
  value: number;
}

// Getters, not values: this object is built while the module is evaluated, so resolving per
// read is what lets a tab label follow a language change instead of freezing at import.
export const FORM_TABS = {
  personaInformation: {
    id: 'persona-information',
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.personaInformation');
    },
    value: 0,
  },
  profileMedia: {
    id: 'profile-media',
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.profileMedia');
    },
    value: 1,
  },
  spaceVisibility: {
    id: 'space-visibility',
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.spaceVisibility');
    },
    value: 2,
  },
} as const satisfies Record<string, FormTab>;

export const FORM_TAB_ARRAY = Object.values(FORM_TABS);

/**
 * All required fields for visual indicators (asterisk).
 */
export const REQUIRED_FIELDS: (keyof EditPersonaFormData)[] = [
  'segment',
  'quote',
  'biographyItems',
  'age',
  'gender',
  'location',
  'education',
  'familyComposition',
  'annualIncome',
  'lifeStage',
  'caresAbout',
  'channels',
  'pains',
  'gains',
  'spaceVisibility',
];

export function isFieldRequired(fieldName: keyof EditPersonaFormData): boolean {
  return REQUIRED_FIELDS.includes(fieldName);
}

/**
 * Human-readable label + owning tab for each form field. Used to build a
 * helpful validation error ("Quote, Age are required") and to navigate the
 * user to the tab that holds the first failing field.
 */
// Labels are getters for the same reason FORM_TABS' are: resolving per read is
// what lets a label follow a language change instead of freezing at import.
export const FIELD_META: Record<keyof EditPersonaFormData, { label: string; tab: number }> = {
  personaTitle: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.personaTitle');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  segment: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.segment');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  quote: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.quote');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  interests: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.interests');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  biographyItems: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.biography');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  age: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.age');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  gender: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.gender');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  location: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.location');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  education: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.education');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  familyComposition: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.familyComposition');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  annualIncome: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.annualIncome');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  lifeStage: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.lifeStage');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  caresAbout: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.caresAbout');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  channels: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.channels');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  pains: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.pains');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  gains: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.gains');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  photoAssetId: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.profileImage');
    },
    tab: FORM_TABS.profileMedia.value,
  },
  mouseOverAssetId: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.thumbnailVideo');
    },
    tab: FORM_TABS.profileMedia.value,
  },
  videoAssetId: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.detailVideo');
    },
    tab: FORM_TABS.profileMedia.value,
  },
  gwiId: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.gwiAudience');
    },
    tab: FORM_TABS.personaInformation.value,
  },
  spaceVisibility: {
    get label() {
      return i18n.t('audiences:audiences.editPersonaForm.spaceVisibility');
    },
    tab: FORM_TABS.spaceVisibility.value,
  },
  workspaceIds: {
    get label() {
      return i18n.t('common:components.workspacesField.workspaces');
    },
    tab: FORM_TABS.spaceVisibility.value,
  },
};

type ArrayFieldName = 'biographyItems' | 'caresAbout' | 'channels' | 'pains' | 'gains';

/* eslint-disable i18next/no-literal-string -- form field names */
const REQUIRED_NESTED_FIELDS: Record<ArrayFieldName, string[]> = {
  biographyItems: ['description'],
  caresAbout: ['item', 'description'],
  channels: ['description'],
  pains: ['description'],
  gains: ['description'],
};
/* eslint-enable i18next/no-literal-string */

export function isNestedFieldRequired(arrayField: ArrayFieldName, nestedField: string): boolean {
  return REQUIRED_NESTED_FIELDS[arrayField].includes(nestedField);
}

/**
 * Map PersonaDto to form data for editing.
 */
export function mapPersonaDtoToFormData(persona: PersonaDto): EditPersonaFormData {
  const demographics = persona.demographicsData?.data;
  const psychometrics = persona.psychometricsData;
  const socialMedia = persona.socialMediaData?.data.platforms ?? [];

  // Compose location from city, region, country
  const locationParts = [demographics?.city, demographics?.region, demographics?.country].filter(
    Boolean,
  );
  const location = locationParts.join(', ');

  // Create lookup map from biography items with data
  const biographyItemsMap = new Map(
    getBiographyItems(persona).map((item) => [item.label, item.value]),
  );

  // Always include all 4 fixed labels, using empty description if no data
  const biographyItems = BIOGRAPHY_ITEM_LABELS.map((label) => ({
    item: label,
    description: biographyItemsMap.get(label) ?? '',
  }));

  // Map CFC caresAbout
  const cfcCaresAbout = psychometrics?.data.CFC.caresAbout ?? [];
  const caresAbout =
    cfcCaresAbout.length > 0
      ? cfcCaresAbout.map((c) => ({
          item: c.title,
          description: c.description,
        }))
      : [
          {
            item: '',
            description: '',
          },
        ];

  // Map CFC pains
  const cfcPains = psychometrics?.data.CFC.pains ?? [];
  const pains =
    cfcPains.length > 0
      ? cfcPains.map((p) => ({
          description: p,
        }))
      : [
          {
            description: '',
          },
        ];

  // Map CFC gains
  const cfcGains = psychometrics?.data.CFC.gains ?? [];
  const gains =
    cfcGains.length > 0
      ? cfcGains.map((g) => ({
          description: g,
        }))
      : [
          {
            description: '',
          },
        ];

  const channels = socialMedia.map((platform) => ({
    platformName: platform.platformName,
    sharePercent: platform.usageLikelihood,
    description: platform.engagementStyle,
  }));

  // The granted workspaces are not on the persona, so this is only the coarse pre-query seed:
  // org-wide or not. `useEditPersonaForm` replaces it with the real scope, and with the
  // `workspaces` choice, once GET /audience/:id/workspaces answers. Staying on the two values
  // that predate the picker is what keeps this correct when the flag is off, where `workspaces`
  // is not among the options offered.
  let spaceVisibility: SpaceVisibilityValue | null = null;
  if (persona.type === 'Scoped') {
    spaceVisibility = persona.visibility === 'organization' ? 'organization' : 'workspace';
  }

  return {
    personaTitle: persona.personaTitle ?? '',
    segment: persona.segmentName ?? '',
    quote: persona.personaQuote ?? '',
    interests: demographics?.interests ?? [],
    biographyItems,
    age: mapAgeToRange(demographics?.age ?? ''),
    gender: normalizeGender(demographics?.gender ?? ''),
    location,
    education: demographics?.education ?? '',
    familyComposition: demographics?.homeComposition ?? '',
    annualIncome: demographics?.income ?? '',
    lifeStage: demographics?.lifestage ?? '',
    caresAbout,
    channels,
    pains,
    gains,
    photoAssetId: persona.photoAssetId ?? null,
    mouseOverAssetId: persona.mouseOverAssetId ?? null,
    videoAssetId: persona.videoAssetId ?? null,
    gwiId: persona.gwiId ?? null,
    spaceVisibility,
    workspaceIds: [],
  };
}

/**
 * Get biography item description by label.
 */
function getBiographyDescription(
  biographyItems: EditPersonaFormData['biographyItems'],
  label: string,
): string {
  return biographyItems.find((b) => b.item === label)?.description ?? '';
}

/**
 * Split a sentence-joined string back into an array.
 * Reverses formatSentences which joins with ". "
 */
function splitSentences(text: string): string[] {
  if (!text.trim()) return [];
  return text
    .split(/\.\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Map form data to UpdatePersonaDto for API submission.
 * Merges form-edited fields with non-editable fields from the original persona.
 */
export function mapFormDataToUpdatePayload(
  formData: EditPersonaFormData,
  persona: PersonaDto,
): UpdatePersonaDto {
  // Parse location (assumes "city, region, country" format)
  const locationParts = formData.location.split(',').map((p) => p.trim());
  const city = locationParts[0] ?? '';
  const region = locationParts[1] ?? '';
  const country = locationParts[2] ?? '';

  // Biography items map to specific API fields
  /* eslint-disable i18next/no-literal-string -- biography discriminators, each routing to a different API field */
  const careerDescription = getBiographyDescription(formData.biographyItems, 'Career');
  const lifestyle = splitSentences(getBiographyDescription(formData.biographyItems, 'Lifestyle'));
  const socialMediaCharacteristics = splitSentences(
    getBiographyDescription(formData.biographyItems, 'Social Media'),
  );
  const socialValues = splitSentences(getBiographyDescription(formData.biographyItems, 'Values'));
  /* eslint-enable i18next/no-literal-string */

  // Extract original data for non-editable fields
  const originalDemographics = persona.demographicsData;
  const originalDemographicsData = originalDemographics?.data;
  const originalSocialMedia = persona.socialMediaData;
  const originalSocialMediaPlatforms = originalSocialMedia?.data.platforms ?? [];
  const originalPsychometrics = persona.psychometricsData;
  const originalCFC = originalPsychometrics?.data.CFC;

  // Map channels with original platform data for non-editable fields
  const platforms = formData.channels.map((c) => {
    const originalPlatform = originalSocialMediaPlatforms.find(
      (p) => p.platformName === c.platformName,
    );
    return {
      platformName: c.platformName,
      usageLikelihood: c.sharePercent,
      engagementStyle: c.description,
      // Non-editable fields from original
      usageJustification: originalPlatform?.usageJustification ?? '',
      contentConsumption: originalPlatform?.contentConsumption ?? '',
      contentCreation: originalPlatform?.contentCreation ?? '',
      keyInfluencersOrAccounts: originalPlatform?.keyInfluencersOrAccounts ?? [],
      affinityIndex: originalPlatform?.affinityIndex,
      shortDescription: originalPlatform?.shortDescription,
    };
  });

  return {
    demographicsData: {
      data: {
        // The form no longer edits names, but the API replaces the whole block, so the
        // stored ones are sent back unchanged rather than wiped.
        firstName: originalDemographicsData?.firstName,
        lastName: originalDemographicsData?.lastName,
        // Form-editable fields
        age: formData.age,
        gender: formData.gender,
        country,
        region,
        city,
        education: formData.education,
        lifestage: formData.lifeStage,
        income: formData.annualIncome,
        homeComposition: formData.familyComposition,
        interests: formData.interests,
        careerDescription,
        // Non-editable fields from original
        urbanicity: originalDemographicsData?.urbanicity ?? '',
        profession: originalDemographicsData?.profession ?? '',
        languages: originalDemographicsData?.languages ?? [],
        incomeCurrency: originalDemographicsData?.incomeCurrency ?? '',
        consumerBehavior: originalDemographicsData?.consumerBehavior ?? [],
      },
      // Non-editable top-level fields
      confidence: originalDemographics?.confidence ?? '',
      characteristics: originalDemographics?.characteristics ?? [],
      citations: originalDemographics?.citations ?? [],
      rationale: originalDemographics?.rationale,
    },
    socialMediaData: {
      data: {
        platforms,
        mediaConsumption: originalSocialMedia?.data.mediaConsumption,
      },
      characteristics: socialMediaCharacteristics,
      // Non-editable top-level fields
      confidence: originalSocialMedia?.confidence ?? '',
      socialMediaUsageSummary: originalSocialMedia?.socialMediaUsageSummary ?? '',
      citations: originalSocialMedia?.citations ?? [],
    },
    psychometricsData: {
      data: {
        CFC: {
          // Form-editable fields
          caresAbout: formData.caresAbout.map((c) => ({
            title: c.item,
            description: c.description,
          })),
          pains: formData.pains.map((p) => p.description),
          gains: formData.gains.map((g) => g.description),
          cultureNorms: {
            lifestyle,
            socialValues,
          },
          // Non-editable fields from original

          constraintsTradeoffs: originalCFC?.constraintsTradeoffs,
          mediaDiet: originalCFC?.mediaDiet,
          styleProfile: originalCFC?.styleProfile,
          knowledgeState: originalCFC?.knowledgeState,
          affectProfile: originalCFC?.affectProfile,
        },
        // Non-editable psychometrics sections
        bigFive: originalPsychometrics?.data.bigFive,
        // Non-editable: preserve the psychographics block (coreValues,
        // needsAndMotivations, painPoints, lifestyle, aspirations, behaviors)
        // rendered by PersonaDetailsSection. Omitting it made the PUT wipe it.
        psychographics: originalPsychometrics?.data.psychographics,
      },
      // Non-editable top-level fields
      confidence: originalPsychometrics?.confidence ?? '',
      characteristics: originalPsychometrics?.characteristics ?? [],
      citations: originalPsychometrics?.citations ?? [],
    },
    segmentName: formData.segment || undefined,
    personaQuote: formData.quote,
    personaTitle: formData.personaTitle || undefined,
    photoAssetId: formData.photoAssetId ?? undefined,
    mouseOverAssetId: formData.mouseOverAssetId ?? undefined,
    videoAssetId: formData.videoAssetId ?? undefined,
    gwiId:
      formData.gwiId === undefined ? undefined : formData.gwiId?.trim() ? formData.gwiId : null,
  } as UpdatePersonaDto;
}
